import { noop } from 'lodash';

import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { VisitSettlement, VisitSettlementQuery } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';

import { useCqrsFind } from '@dreryk/command-query-react-common';
import { FormController, FormTabs } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { VisitDetailsFormBean } from '../../shared/form/visitDetails/visitDetails.types';
import { VisitSettlementController } from '../../visitSettlement.types';

import { VisitSettlementPaymentAction } from './payment/VisitSettlementPaymentAction';
import { VisitSettlementFormTab, VisitSettlementFormTabLoader } from './VisitSettlementFormTab';
import { VisitSettlementFormTabLabel } from './VisitSettlementFormTabLabel';

type UseVisitSettlementFormTabOptions = {
   controller: VisitSettlementController;
   appointmentId: number;
   practitionerId: number;
   visitDetails: Omit<VisitDetailsFormBean, 'practitioner'>;
   onSettlementModalOpen: VoidFunction;
};

type UseVisitSettlementPaymentDropdownOptionReturnFn = ReturnType<typeof FormTabs.Tab> | undefined;

export const useVisitSettlementFormTab = (
   options: UseVisitSettlementFormTabOptions
): UseVisitSettlementPaymentDropdownOptionReturnFn => {
   const privateMedicalServicesReadOnlyAvailable = protectFeature('privateMedicalServicesReadOnly');

   const { data: readOnlyVisitSettlement } = useCqrsFind<VisitSettlement>(
      () => {
         const query = new VisitSettlementQuery();
         query.purchaseIds = [PurchaseId.forAppointment(AppointmentId.of(options.appointmentId))];
         return query;
      },
      {
         enabled: privateMedicalServicesReadOnlyAvailable,
         onUnexpectedError: noop,
      }
   );

   const readOnlyHasMedicalServices =
      privateMedicalServicesReadOnlyAvailable && (readOnlyVisitSettlement?.medicalServices.length ?? 0) > 0;

   if (!protectFeature('performingPrivateMedicalServices') && !readOnlyHasMedicalServices) {
      return undefined;
   }

   return (
      <FormTabs.Tab
         register={options.controller.register}
         id={'visitSettlement'}
         label={() => <VisitSettlementFormTabLabel control={options.controller.control} />}
      >
         <>
            <FormController controller={options.controller.register('visitSettlement')} />
            <FormController controller={options.controller.register('visitSettlement.id')} />
            <FormController controller={options.controller.register('visitSettlement.priceChangeReason')} />
            <FormController controller={options.controller.register('visitSettlement.editable')} />
            <FormController controller={options.controller.register('visitSettlement.ownedByCurrentUser')} />
            <FormController controller={options.controller.register('visitSettlement.advancePaymentAmount')} />
            <FormController controller={options.controller.register('visitSettlement.advancePaymentMethod')} />
            <VisitSettlementFormTabLoader appointmentId={options.appointmentId} controller={options.controller}>
               {({ adjustmentBy, invoiceProviderIntegrationEnabled }) => (
                  <>
                     <VisitSettlementFormTab
                        register={options.controller.register}
                        control={options.controller.control}
                        practitionerId={options.practitionerId}
                        appointmentId={options.appointmentId}
                        adjustmentBy={adjustmentBy}
                     />
                     {privateMedicalServicesReadOnlyAvailable ? null : (
                        <VisitSettlementPaymentAction
                           invoiceProviderIntegrationEnabled={invoiceProviderIntegrationEnabled}
                           appointmentId={options.appointmentId}
                           practitionerId={options.practitionerId}
                           visitDetails={options.visitDetails}
                           control={options.controller.control}
                           onSettlementModalOpen={options.onSettlementModalOpen}
                        />
                     )}
                  </>
               )}
            </VisitSettlementFormTabLoader>
         </>
      </FormTabs.Tab>
   );
};
